import type { WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { useListsStore } from '@shared/model'

import { useDisciplineFilesStore } from './files/store'
import { useDisciplineFiltersDraft } from './discipline-filters-draft.hook'
import {
  disciplineFilesFiltersToQuery,
  fromDisciplineFilesDateRange,
  toDisciplineFilesDateRange,
} from '../lib/discipline-files-query'

interface FiltersDisciplineFilesReturn {
  refDateRange: WritableComputedRef<Date[] | null>
  refCategories: WritableComputedRef<string[]>
  categoryOptions: ComputedRef<AppSelectOption[]>
  isLoading: ComputedRef<boolean>
  search: () => void
  clearFilters: () => void
}

export const useFiltersDisciplineFiles = (): FiltersDisciplineFilesReturn => {
  const store = useDisciplineFilesStore()
  const listsStore = useListsStore()

  const { refDraft, isLoading, patchDraft, search, clearFilters } = useDisciplineFiltersDraft({
    applied: () => store.getFilters,
    onApply: filters => store.setFilters(filters),
    onReset: () => store.resetFilters(),
    toQuery: disciplineFilesFiltersToQuery,
    loading: () => store.isLoading,
  })

  const refDateRange = computed<Date[] | null>({
    get: () => toDisciplineFilesDateRange(refDraft.value.from, refDraft.value.to),
    set: (value) => {
      patchDraft(fromDisciplineFilesDateRange(value))
    },
  })

  const refCategories = computed<string[]>({
    get: () => refDraft.value.categories,
    set: (value) => {
      patchDraft({ categories: value })
    },
  })

  const categoryOptions = computed<AppSelectOption[]>(() =>
    Object.entries(listsStore.getLists?.file.category ?? {}).map(([value, text]) => ({ value, text })))

  return {
    refDateRange,
    refCategories,
    categoryOptions,
    isLoading,
    search,
    clearFilters,
  }
}
